import React from "react";
import { Box, Chip, Stack, Typography, Paper } from "@mui/material";
import {
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  CardMembership as MembershipIcon,
  Warning as InactiveIcon,
} from "@mui/icons-material";

export default function PatientHeader({ patient }) {
  if (!patient) return null;

  const isInactive = patient.patientStatus === 1;

  return (
    <Box>
      {/* Alert for inactive patients */}
      {isInactive && (
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: "error.50",
            borderLeft: 4,
            borderLeftColor: "error.main",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <InactiveIcon color="error" />
            <Typography variant="body2" color="error.dark" fontWeight={600}>
              This patient is currently INACTIVE
            </Typography>
          </Stack>
        </Paper>
      )}

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        {/* Patient Info */}
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <PersonIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" color="primary" fontWeight={700}>
              Patient Information
            </Typography>
          </Stack>
          <Typography variant="h6" fontWeight={800}>
            {patient.memberFullName || "Unknown patient"}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>CIP:</strong> {patient.cipId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Gender:</strong> {patient.gender || "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>DOB:</strong> {patient.dateOfBirth || "-"}
            </Typography>
          </Stack>
        </Paper>

        {/* Membership Info */}
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <MembershipIcon color="secondary" fontSize="small" />
            <Typography variant="subtitle2" color="secondary" fontWeight={700}>
              Membership
            </Typography>
          </Stack>
          <Typography variant="body2" fontWeight={600}>
            {patient.networkName || "No Network"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {patient.memberPlan || "No Plan"}
            {patient.metalLevel && (
              <Chip
                label={patient.metalLevel}
                size="small"
                color="info"
                variant="outlined"
                sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
              />
            )}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              label={isInactive ? "Inactive" : "Active"}
              size="small"
              color={isInactive ? "error" : "success"}
              variant={isInactive ? "filled" : "outlined"}
            />
          </Stack>
        </Paper>

        {/* Provider Info */}
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <HospitalIcon color="info" fontSize="small" />
            <Typography variant="subtitle2" color="info.main" fontWeight={700}>
              Provider & Location
            </Typography>
          </Stack>
          <Typography variant="body2" fontWeight={600}>
            {patient.pcpName || "Unassigned PCP"}
          </Typography>
          {patient.pcpId && (
            <Typography variant="caption" color="text.secondary">
              ID: {patient.pcpId}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {patient.medicalCenter || "No Medical Center"}
          </Typography>
        </Paper>

        {/* Coding Opportunities Count */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            minWidth: 120,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "primary.50",
          }}
        >
          <Typography variant="h4" fontWeight={900} color="primary">
            {patient.codingOpCount ?? 0}
          </Typography>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Coding Opportunities
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
}
